/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import { Component, OnDestroy } from "@angular/core";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { Subject, combineLatest, map, startWith, switchMap } from "rxjs";
import {
    ConfirmationModalComponent, ConfirmationModalData, StrongConfirmationModalComponent, StrongConfirmationModalData,
    ButtonComponent,
    MenuItem,
} from "typedb-cloud-framework";
import {
    Cluster, isInConnectableState,
    isInDestroyableState,
    isInResumableState,
    isInPausableState
} from "../../../concept/cluster";
import { LOADING, NO_ITEMS_SELECTED } from "../../../framework/strings";
import { ClusterApi } from "../../../service/cluster/cluster-api.service";
import { confirmationMessage, strongConfirmationStringOf } from "../../../service/dialog.service";
import { TeamController } from "../../../service/team/team-controller.service";
import { SnackbarService } from "../../../service/snackbar.service";
import { AsyncPipe } from "@angular/common";
import { resourceAvailabilityStreamOf, PageScaffoldComponent } from "../../scaffold/page/page-scaffold.component";
import { ApplicationState } from "../../../service/application-state.service";
import { ClustersTableComponent } from "../table/clusters-table.component";
import { MatDialog } from "@angular/material/dialog";
import { TablePaginatorComponent, TableToolbarComponent } from "../../../framework/table";
import { MatTooltipModule } from "@angular/material/tooltip";
import { AccessLevel, hasAdminAccess, hasReadAccess, hasWriteAccess } from "../../../concept/iam";
import { clusterDetailsPath } from "../../../routing/resource-paths";
import { PauseResumeModalComponent, PauseResumeModalData } from "../pause-resume/pause-resume-modal.component";

@Component({
    selector: "tp-clusters-page",
    templateUrl: "./clusters-page.component.html",
    styleUrls: ["./clusters-page.component.scss"],
    imports: [
        PageScaffoldComponent, AsyncPipe, RouterLink, TableToolbarComponent,
        ClustersTableComponent, TablePaginatorComponent, ButtonComponent, MatTooltipModule,
    ]
})
export class ClustersPageComponent implements OnDestroy {
    readonly team = this.app.requireCurrentTeam();

    private readonly unsub$ = new Subject<void>();
    readonly clusters = this.teamCtl.clustersTable(this.unsub$);
    readonly availability$ = resourceAvailabilityStreamOf(this.clusters);

    readonly writableSpaces$ = this.teamCtl.listWritableSpacesSnapshot();
    readonly cannotDeployReason$ = this.writableSpaces$.pipe(map(writableSpaces => {
        if (!writableSpaces.length) return "You need write access to at least one space to deploy a new cluster";
        return null;
    }), startWith(LOADING));
    readonly cannotPauseReason$ = this.clusters.hasWriteAccessToSelected$.pipe(map(canWrite => {
        if (!this.clusters.isAnySelected()) return NO_ITEMS_SELECTED;
        if (!canWrite) return "You don't have write access to one or more of the selected cluster(s)";
        if (this.clusters.selected.some(x => !isInPausableState(x))) return "A selected cluster is not running";
        return null;
    }), startWith(LOADING));
    readonly cannotResumeReason$ = this.clusters.hasWriteAccessToSelected$.pipe(map(canWrite => {
        if (!this.clusters.isAnySelected()) return NO_ITEMS_SELECTED;
        if (!canWrite) return "You don't have write access to one or more of the selected cluster(s)";
        if (this.clusters.selected.some(x => !isInResumableState(x))) return "A selected cluster is not paused";
        return null;
    }), startWith(LOADING));
    readonly deployEnabled$ = this.cannotDeployReason$.pipe(map(x => !x));
    readonly pauseEnabled$ = this.cannotPauseReason$.pipe(map(x => !x));
    readonly resumeEnabled$ = this.cannotResumeReason$.pipe(map(x => !x));
    readonly teamHasCluster$ = this.teamCtl.clustersTable(this.unsub$).items$.pipe(map(x => x.length > 0));

    readonly clusterMenuItems: (cluster: Cluster, accessLevel: AccessLevel) => MenuItem[] = (cluster, accessLevel) => [
        {
            label: "View details",
            action: () => this.router.navigate([clusterDetailsPath(cluster, this.team)]),
            disabled: !hasReadAccess(accessLevel),
            disabledReason: !hasReadAccess(accessLevel) ? "You don't have read access to this cluster" : undefined,
        },
        {
            label: "Pause",
            action: () => this.openPauseResumeModalSingle("pause", cluster),
            disabled: !hasWriteAccess(accessLevel) || !isInPausableState(cluster),
            disabledReason: !hasWriteAccess(accessLevel) ? "You don't have write access to this cluster" :
                (!isInPausableState(cluster) ? "This cluster is not running" : undefined)
        },
        {
            label: "Resume",
            action: () => this.openPauseResumeModalSingle("resume", cluster),
            disabled: !hasWriteAccess(accessLevel) || !isInResumableState(cluster),
            disabledReason: !hasWriteAccess(accessLevel) ? "You don't have write access to this cluster" :
                (!isInResumableState(cluster) ? "This cluster is not paused" : undefined)
        },
        {
            label: "Destroy",
            action: () => this.openDestroyModal(cluster),
            disabled: !hasAdminAccess(accessLevel) || !isInDestroyableState(cluster),
            disabledReason: !hasWriteAccess(accessLevel) ? "You don't have admin access to this cluster" :
                (!isInDestroyableState(cluster) ? "This cluster is not in a destroyable state" : undefined),
            dangerous: true
        },
    ];

    constructor(
        private clusterApi: ClusterApi, public route: ActivatedRoute, private snackbar: SnackbarService,
        private app: ApplicationState, private teamCtl: TeamController, private dialog: MatDialog,
        private router: Router,
    ) {}

    ngOnDestroy() {
        this.unsub$.next();
    }

    openPauseResumeModalSingle(action: "pause" | "resume", cluster: Cluster) {
        const modal = this.dialog.open<PauseResumeModalComponent, PauseResumeModalData>(PauseResumeModalComponent, {
            data: { cluster, action }
        }).componentInstance;
        modal.confirmed.pipe(
            switchMap(() => {
                switch (action) {
                    case "pause": return this.clusterApi.pauseCluster(cluster.uuid);
                    case "resume": return this.clusterApi.resumeCluster(cluster.uuid);
                }
            }),
        ).subscribe({
            next: () => {
                modal.close();
                switch (action) {
                    case "pause": this.snackbar.success(`'${cluster.id}' is now being paused.`); break;
                    case "resume": this.snackbar.success(`'${cluster.id}' is now being resumed.`); break;
                }
            },
            error: () => {
                modal.isSubmitting$.next(false);
            },
        });
    }

    openPauseResumeModalForSelection(action: "pause" | "resume") {
        const clusters = [...this.clusters.selected];
        const title = action === "resume"
            ? clusters.length === 1 ? "Resume cluster" : "Resume clusters"
            : clusters.length === 1 ? "Pause cluster" : "Pause clusters";
        const modal = this.dialog.open<ConfirmationModalComponent, ConfirmationModalData>(ConfirmationModalComponent, {
            data: {
                title: title,
                body: confirmationMessage(action, clusters, "cluster"),
                confirmText: title,
                confirmButtonStyle: action === "resume" ? "primary-outline green stroke" : "primary-outline red stroke",
            },
        }).componentInstance;
        modal.confirmed.pipe(
            switchMap(() => {
                switch (action) {
                    case "pause": return combineLatest(clusters.map(x => this.clusterApi.pauseCluster(x.uuid)));
                    case "resume": return combineLatest(clusters.map(x => this.clusterApi.resumeCluster(x.uuid)));
                }
            }),
        ).subscribe({
            next: () => {
                modal.close();
                switch (action) {
                    case "pause": this.snackbar.success(`Cluster(s) now being paused.`); break;
                    case "resume": this.snackbar.success(`Cluster(s) now being resumed.`); break;
                }
            },
            error: () => {
                modal.isSubmitting$.next(false);
            },
        });
    }

    openDestroyModal(cluster: Cluster) {
        const modal = this.dialog.open<StrongConfirmationModalComponent, StrongConfirmationModalData>(StrongConfirmationModalComponent, {
            data: {
                title: "Destroy cluster",
                body: confirmationMessage("delete", [cluster], "cluster") + ` All of its data and backups will be permanently erased. This action cannot be undone!`,
                confirmText: "Destroy cluster",
                strongConfirmationString: strongConfirmationStringOf([cluster]),
            },
        }).componentInstance;
        modal.confirmed.pipe(switchMap(() => this.clusterApi.destroyCluster(cluster.uuid))).subscribe({
            next: () => {
                modal.close();
                this.snackbar.success(`Cluster now being destroyed.`);
            },
            error: () => {
                modal.isSubmitting$.next(false);
            },
        });
    }
}
